import React from "react";
import { Link } from "react-router-dom";
import { Icon, IconName } from "./Icon";
import { MarketingHeader } from "./MarketingHeader";
import { PageMeta } from "./PageMeta";

/**
 * Public marketing landing at `/`. Served to unauthenticated visitors; auth'd
 * users get redirected to /dashboard/control by App.tsx.
 *
 * Goals:
 *  - Crawlable, SEO-friendly content (real copy, real structure, real links).
 *  - Multiple "Login with Twitch" CTAs at different scroll depths.
 *  - Voice consistent with the rest of the site: confident, slightly edgy,
 *    no marketing fluff. ("HAL doesn't dilute itself for anyone.")
 *  - JSON-LD WebSite + WebPage so Google can build a richer SERP card.
 */

interface MarketingLandingProps {
  onLogin: () => void;
}

const PURPLE = "#9146FF";
const CYAN = "#00D4FF";
const BG = "#0a0a0a";
const TEXT = "#e2e8f0";
const MUTED = "#a0aec0";

const sectionPad = (isMobile: boolean): React.CSSProperties => ({
  padding: isMobile ? "64px 20px" : "96px 60px",
  maxWidth: "1200px",
  margin: "0 auto",
});

const gradientText: React.CSSProperties = {
  background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

const ctaButton = (variant: "primary" | "ghost"): React.CSSProperties =>
  variant === "primary"
    ? {
        background: `linear-gradient(135deg, ${PURPLE} 0%, ${CYAN} 100%)`,
        color: "white",
        border: "none",
        padding: "14px 28px",
        fontSize: "16px",
        fontWeight: 600,
        borderRadius: "12px",
        cursor: "pointer",
        boxShadow: "0 8px 25px rgba(145, 70, 255, 0.4)",
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
      }
    : {
        background: "transparent",
        color: TEXT,
        border: `1px solid ${MUTED}`,
        padding: "14px 28px",
        fontSize: "16px",
        fontWeight: 500,
        borderRadius: "12px",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
      };

const feature = (
  icon: IconName,
  title: string,
  body: string,
): { icon: IconName; title: string; body: string } => ({ icon, title, body });

const FEATURES = [
  feature(
    "users-round",
    "Dynamic personalities",
    "Choose from dozens of AI personalities — DJ, Musician, Gamer, Tech Expert, and more. Or shape your own. HAL stays in character all stream long.",
  ),
  feature(
    "brain",
    "Advanced memory",
    "Persistent conversation threads, sentiment analysis, and trend detection across every interaction. HAL remembers your regulars — and what they're like to be around.",
  ),
  feature(
    "zap",
    "Real-time context",
    "Feed in your current song, BPM, game title, weather, or any custom data. HAL responds in the moment, not from a stale knowledge cutoff.",
  ),
  feature(
    "bar-chart-3",
    "Analytics & triggers",
    "Audience insights, automated responses, and intelligent conversation management — so you can keep streaming while HAL handles the room.",
  ),
];

const HOW_IT_WORKS = [
  {
    n: "1",
    title: "Log in with Twitch",
    body: "OAuth in two clicks. HAL only asks for what it needs to read chat and post as the bot.",
  },
  {
    n: "2",
    title: "Pick a personality",
    body: "Start from a preset or write your own. Personality stays consistent across sessions, but its memory grows with every stream.",
  },
  {
    n: "3",
    title: "HAL joins your chat",
    body: "It connects to your channel, learns your regulars, and starts responding. You stream. It handles the room.",
  },
];

const DIFFERENTIATORS: Array<{ icon: IconName; line: string }> = [
  {
    icon: "shield-check",
    line: "Your data, your channel. No selling viewer info, no third-party ad tracking.",
  },
  {
    icon: "sliders",
    line: "Configurable everything — personality, triggers, response style, mod controls.",
  },
  {
    icon: "message-square",
    line: "Doesn't break character mid-stream. HAL doesn't dilute itself for anyone.",
  },
  {
    icon: "brain",
    line: "Memory that actually compounds. The more HAL sees your community, the better it gets.",
  },
];

// No SearchAction here: hal8580.com has no site search, and declaring one
// trips Google's rich-results validation (flagged by Ahrefs site audit).
const jsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  url: "https://hal8580.com/",
  name: "HAL 8580",
  description:
    "AI-powered Twitch chat bot that remembers viewers, runs commands, and stays in character across your streams.",
};

export const MarketingLanding: React.FC<MarketingLandingProps> = ({
  onLogin,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 860);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  return (
    <div
      style={{
        backgroundColor: BG,
        color: TEXT,
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        minHeight: "100vh",
        width: "100%",
      }}
    >
      <PageMeta
        title="HAL 8580 — AI Twitch Chat Bot for Streamers"
        description="An AI-powered Twitch chat bot that remembers your viewers and runs commands. Dozens of personalities. Real-time context. Built for streamers."
        path="/"
        jsonLd={jsonLd}
      />

      <MarketingHeader onLogin={onLogin} />

      {/* Hero */}
      <section
        style={{
          ...sectionPad(isMobile),
          paddingTop: isMobile ? "32px" : "48px",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.1fr 1fr",
          gap: isMobile ? "32px" : "64px",
          alignItems: "center",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: isMobile ? "44px" : "68px",
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-2px",
              margin: "0 0 24px 0",
              ...gradientText,
            }}
          >
            The AI chat bot that actually shows up.
          </h1>
          <p
            style={{
              fontSize: isMobile ? "18px" : "22px",
              color: MUTED,
              lineHeight: 1.5,
              margin: "0 0 32px 0",
              fontWeight: 300,
              maxWidth: "560px",
            }}
          >
            HAL 8580 joins your Twitch chat with a personality you choose,
            remembers your regulars, reacts to what’s happening on stream, and
            doesn’t dilute itself for anyone.
          </p>
          <div
            style={{
              display: "flex",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <button onClick={onLogin} style={ctaButton("primary")}>
              <Icon name="gamepad-2" size={18} color="white" />
              Log in with Twitch
            </button>
            <a
              href="#how"
              style={{ ...ctaButton("ghost"), textDecoration: "none" }}
            >
              See how it works
              <Icon name="arrow-right" size={16} />
            </a>
          </div>
          <p
            style={{
              color: "#4a5568",
              fontSize: "13px",
              marginTop: "20px",
              fontWeight: 300,
            }}
          >
            Free to try. Two-click OAuth, no credit card.
          </p>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src="/assets/hal-portrait.jpg"
            alt="HAL 8580 — AI chat bot"
            style={{
              maxWidth: "100%",
              width: isMobile ? "260px" : "440px",
              height: "auto",
              borderRadius: "16px",
              boxShadow: `0 0 80px 20px rgba(145, 70, 255, 0.35)`,
            }}
          />
        </div>
      </section>

      {/* Features */}
      <section id="features" style={sectionPad(isMobile)}>
        <h2
          style={{
            fontSize: isMobile ? "32px" : "44px",
            fontWeight: 700,
            margin: "0 0 16px 0",
            letterSpacing: "-1px",
          }}
        >
          What you actually get.
        </h2>
        <p
          style={{
            color: MUTED,
            fontSize: "18px",
            margin: "0 0 48px 0",
            maxWidth: "640px",
            fontWeight: 300,
          }}
        >
          Not a script. Not a templated response engine. An actual companion
          that learns your community.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "32px",
          }}
        >
          {FEATURES.map((f) => (
            <div
              key={f.title}
              style={{
                display: "flex",
                gap: "20px",
                padding: "24px",
                borderRadius: "16px",
                background: "rgba(145, 70, 255, 0.04)",
                border: "1px solid rgba(145, 70, 255, 0.12)",
              }}
            >
              <div
                style={{
                  color: PURPLE,
                  display: "flex",
                  alignItems: "flex-start",
                  paddingTop: "2px",
                  flexShrink: 0,
                }}
              >
                <Icon name={f.icon} size={28} />
              </div>
              <div>
                <h3
                  style={{
                    color: TEXT,
                    margin: "0 0 8px 0",
                    fontSize: "20px",
                    fontWeight: 600,
                  }}
                >
                  {f.title}
                </h3>
                <p
                  style={{
                    color: "#cbd5e0",
                    margin: 0,
                    fontSize: "15px",
                    lineHeight: 1.6,
                    fontWeight: 300,
                  }}
                >
                  {f.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section
        id="how"
        style={{
          ...sectionPad(isMobile),
          background:
            "linear-gradient(180deg, transparent 0%, rgba(145, 70, 255, 0.03) 50%, transparent 100%)",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "32px" : "44px",
            fontWeight: 700,
            margin: "0 0 48px 0",
            letterSpacing: "-1px",
          }}
        >
          Up and running in two minutes.
        </h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "32px",
          }}
        >
          {HOW_IT_WORKS.map((step) => (
            <div key={step.n}>
              <div
                style={{
                  fontSize: "48px",
                  fontWeight: 700,
                  ...gradientText,
                  marginBottom: "12px",
                  letterSpacing: "-1px",
                }}
              >
                {step.n}
              </div>
              <h3
                style={{
                  fontSize: "22px",
                  fontWeight: 600,
                  margin: "0 0 12px 0",
                }}
              >
                {step.title}
              </h3>
              <p
                style={{
                  color: "#cbd5e0",
                  margin: 0,
                  fontSize: "15px",
                  lineHeight: 1.6,
                  fontWeight: 300,
                }}
              >
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Why HAL */}
      <section style={sectionPad(isMobile)}>
        <h2
          style={{
            fontSize: isMobile ? "32px" : "44px",
            fontWeight: 700,
            margin: "0 0 48px 0",
            letterSpacing: "-1px",
          }}
        >
          Built for streamers. Not for spammers.
        </h2>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "20px",
          }}
        >
          {DIFFERENTIATORS.map((d) => (
            <li
              key={d.line}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "16px",
                padding: "16px 0",
              }}
            >
              <div
                style={{
                  color: PURPLE,
                  display: "flex",
                  paddingTop: "2px",
                  flexShrink: 0,
                }}
              >
                <Icon name={d.icon} size={22} />
              </div>
              <span
                style={{
                  color: "#cbd5e0",
                  fontSize: "16px",
                  lineHeight: 1.6,
                  fontWeight: 300,
                }}
              >
                {d.line}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {/* Final CTA */}
      <section
        style={{
          ...sectionPad(isMobile),
          textAlign: "center",
          padding: isMobile ? "64px 20px" : "120px 60px",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? "36px" : "52px",
            fontWeight: 700,
            margin: "0 0 24px 0",
            letterSpacing: "-1.5px",
            ...gradientText,
          }}
        >
          Let HAL handle the chat.
        </h2>
        <p
          style={{
            color: MUTED,
            fontSize: "18px",
            margin: "0 auto 32px",
            maxWidth: "560px",
            lineHeight: 1.5,
            fontWeight: 300,
          }}
        >
          You stream. HAL remembers your regulars, reacts in real time, and
          keeps the room alive — even when you’re heads-down in the game.
        </p>
        <button
          onClick={onLogin}
          style={{
            ...ctaButton("primary"),
            fontSize: "18px",
            padding: "16px 32px",
          }}
        >
          <Icon name="gamepad-2" size={20} color="white" />
          Log in with Twitch
        </button>
        <p
          style={{
            color: "#4a5568",
            fontSize: "13px",
            marginTop: "16px",
            fontWeight: 300,
          }}
        >
          Read the{" "}
          <Link to="/faq" style={{ color: MUTED }}>
            FAQ
          </Link>{" "}
          · See our{" "}
          <Link to="/privacy" style={{ color: MUTED }}>
            privacy policy
          </Link>
        </p>
      </section>
    </div>
  );
};
